/**
 * The Kruskal–Wallis H test for k independent groups, with Dunn's post hoc test.
 *
 * H = 12 / (N(N + 1)) · Σ Rⱼ²/nⱼ − 3(N + 1), divided by the tie correction
 * C = 1 − Σ(t³ − t) / (N³ − N), and referred to χ² with k − 1 df — exactly
 * `scipy.stats.kruskal` and R's `kruskal.test`.
 *
 * Effect size: ε² = H / (N − 1) (Tomczak & Tomczak, 2014), which runs from 0 to 1.
 *
 * Dunn (1964): z = (R̄ᵢ − R̄ⱼ) / SE with the tie-adjusted
 * SE = √([N(N + 1)/12 − Σ(t³ − t) / (12(N − 1))] · (1/nᵢ + 1/nⱼ)), two-sided p, and a
 * Bonferroni adjustment over the k(k − 1)/2 pairs — the defaults of R's `dunn.test`
 * with `method = "bonferroni"` and of `scikit-posthocs.posthoc_dunn`.
 */

import { averageRanks, tieGroupSizes } from "@/lib/stats/descriptive";
import { chiSquareUpper, normalUpper } from "@/lib/stats/distributions";

export interface DunnComparison {
  first: number;
  second: number;
  meanRankDifference: number;
  se: number;
  z: number;
  p: number;
  pAdjusted: number;
}

export interface KruskalWallisResult {
  n: number;
  k: number;
  sizes: number[];
  rankSums: number[];
  meanRanks: number[];
  /** H before the tie correction. */
  hUncorrected: number;
  tieCorrection: number;
  /** H after the tie correction — the reported statistic. */
  h: number;
  df: number;
  p: number;
  epsilonSquared: number;
  tieTerm: number;
  dunn: DunnComparison[];
}

export type KruskalWallisFailure =
  | "too-few-groups"
  | "too-few-values"
  | "all-tied";

export type KruskalWallisOutcome =
  | { ok: true; result: KruskalWallisResult }
  | { ok: false; reason: KruskalWallisFailure };

function dunnComparisons(
  sizes: number[],
  meanRanks: number[],
  n: number,
  tieTerm: number
): DunnComparison[] {
  const m = (sizes.length * (sizes.length - 1)) / 2;
  const base = (n * (n + 1)) / 12 - tieTerm / (12 * (n - 1));
  const comparisons: DunnComparison[] = [];
  for (let i = 0; i < sizes.length; i++) {
    for (let j = i + 1; j < sizes.length; j++) {
      const meanRankDifference = meanRanks[i] - meanRanks[j];
      const se = Math.sqrt(base * (1 / sizes[i] + 1 / sizes[j]));
      const z = meanRankDifference / se;
      const p = Math.min(1, 2 * normalUpper(Math.abs(z)));
      comparisons.push({
        first: i,
        second: j,
        meanRankDifference,
        se,
        z,
        p,
        pAdjusted: Math.min(1, p * m),
      });
    }
  }
  return comparisons;
}

export function kruskalWallis(
  groups: readonly (readonly number[])[]
): KruskalWallisOutcome {
  if (groups.length < 2) {
    return { ok: false, reason: "too-few-groups" };
  }
  if (groups.some((group) => group.length < 1)) {
    return { ok: false, reason: "too-few-values" };
  }
  const all = groups.flat();
  const n = all.length;
  const ranks = averageRanks(all);
  const sizes = groups.map((group) => group.length);
  const rankSums: number[] = [];
  let offset = 0;
  for (const size of sizes) {
    rankSums.push(
      ranks.slice(offset, offset + size).reduce((a, r) => a + r, 0)
    );
    offset += size;
  }
  const tieTerm = tieGroupSizes(all).reduce((acc, t) => acc + t ** 3 - t, 0);
  const tieCorrection = 1 - tieTerm / (n ** 3 - n);
  if (tieCorrection <= 0) {
    return { ok: false, reason: "all-tied" };
  }
  // Clamped: equal mean ranks give an H of zero that rounding can push below it.
  const hUncorrected = Math.max(
    0,
    (12 / (n * (n + 1))) *
      rankSums.reduce((acc, r, i) => acc + (r * r) / sizes[i], 0) -
      3 * (n + 1)
  );
  const h = hUncorrected / tieCorrection;
  const df = groups.length - 1;
  const meanRanks = rankSums.map((r, i) => r / sizes[i]);
  return {
    ok: true,
    result: {
      n,
      k: groups.length,
      sizes,
      rankSums,
      meanRanks,
      hUncorrected,
      tieCorrection,
      h,
      df,
      p: chiSquareUpper(h, df),
      epsilonSquared: h / (n - 1),
      tieTerm,
      dunn: dunnComparisons(sizes, meanRanks, n, tieTerm),
    },
  };
}
